import React, { useMemo, memo, useState } from 'react';
import PropTypes from 'prop-types';
import identity from 'lodash/identity';
import {
  Text,
  Input,
  Button,
  Flex,
  LookupMultiSelect
} from 'onefaceui-react-components';
import { useSelector } from 'react-redux';

import Modal from 'react/components/Modal/Modal';
import { getResourceTo } from 'react/utils/utils';
import { getLookupExtraParams } from 'react/utils/lookupUtils';

function LocalzIntegrationModal({
  isOpen,
  integrationData,
  title,
  message,
  confirmActionCallback,
  cancelActionCallback
}) {
  const { enabled, integration } = useMemo(
    () => JSON.parse(integrationData),
    [integrationData]
  );

  const [formData, setFormData] = useState({
    region: integration.region,
    projectId: integration.projectId,
    apikey: integration.apikey,
    organizations: integration.organizations
  });

  const lookupFields = useMemo(
    () =>
      getLookupExtraParams({
        entity: 'Organization',
        defaultField: 'key'
      }),
    []
  );

  const isInvalidFields =
    !formData.apikey || !formData.projectId || !formData.region;

  const { isConnecting, success, error } = useSelector((state) =>
    state.localzIntegration.get('connectionState')
  );
  const isLoadingIntegration = useSelector((state) =>
    state.localzIntegration.get('isLoadingIntegration')
  );

  const errorMessage = useMemo(
    () => (!isConnecting && error ? error.message : null),
    [error, isConnecting]
  );

  const successMessage = useMemo(
    () => (!isConnecting && !error && success ? success.message : null),
    [error, success, isConnecting]
  );

  const saveIntegrationButtonText = useMemo(
    () =>
      enabled
        ? getResourceTo('default.button.save')
        : getResourceTo(
            'IntegrationModal.ConnectIntegration.ConnectButton',
            integration.name
          ),
    [enabled, integration]
  );

  const handleSubmit = () => {
    confirmActionCallback(formData);
  };

  const handleChangeInput = (e) => {
    const { value } = e.target;

    setFormData((oldState) => ({
      ...oldState,
      [e.target.name]: value
    }));
  };

  const ModalBody = (
    <Flex gap={4} flexDirection="column" className="modalBody">
      <Text size="xs" className="modalBodyMessage">
        {message}
      </Text>

      <Input
        type="text"
        label={getResourceTo(
          'IntegrationModal.ConnectIntegration.Region',
          integration.name
        )}
        name="region"
        onChange={handleChangeInput}
        autoComplete="off"
        value={formData.region}
        required
        disabled={isConnecting}
        className="base-input"
      />
      <Input
        type="text"
        label={getResourceTo('IntegrationModal.ConnectIntegration.ProjectID')}
        name="projectId"
        onChange={handleChangeInput}
        autoComplete="off"
        value={formData.projectId}
        required
        disabled={isConnecting}
        className="base-input"
      />
      <Input
        type="text"
        label={getResourceTo('IntegrationModal.ConnectIntegration.ApiKey')}
        name="apikey"
        onChange={handleChangeInput}
        autoComplete="off"
        value={formData.apikey}
        required
        disabled={isConnecting}
        className="base-input"
      />
      <LookupMultiSelect
        label={getResourceTo(
          'IntegrationModal.ConnectIntegration.ApplyOrganizations'
        )}
        name="organizations"
        defaultField="key"
        displayMode="form"
        onSelectValues={(values) => {
          setFormData((oldState) => ({
            ...oldState,
            organizations: values.map(({ id, key }) => ({ id, key }))
          }));
        }}
        dataTestId="organizations-field"
        autoComplete="off"
        required
        defaultValue={formData.organizations}
        onRemoveItem={(values) => {
           setFormData((oldState) => ({
            ...oldState,
            organizations: values.map(({ id, key }) => ({ id, key }))
          }));
        }}
        onRemoveAll={() => {
          setFormData((oldState) => ({
            ...oldState,
            organizations: []
          }));
        }}
        {...lookupFields}
      />
      {successMessage && (
        <div className="modalMessageSuccess">
          <Text
            size="sm"
            type="success"
            weight="bold"
            className="modalMessageSuccessText"
          >
            <i className="checkIcon fa fa-check" />
            {successMessage ?? 'Success'}
          </Text>
          <Text size="xs">
            {getResourceTo('IntegrationModal.ConnectIntegration.SuccessInfo')}
          </Text>
          <Text size="xs">
            {getResourceTo(
              'IntegrationModal.ConnectIntegration.SuccessSecondaryFeedback'
            )}
          </Text>
        </div>
      )}
      {errorMessage && (
        <Text size="xs" type="danger" className="modalMessageError">
          {errorMessage}
        </Text>
      )}
    </Flex>
  );

  const ModalFooter = (
    <div className={`footerModal ${enabled || success ? 'isConnected' : ''}`}>
      <Button variant="secondary" onClick={cancelActionCallback}>
        {getResourceTo(
          success ? 'default.button.close' : 'IntegrationModal.ButtonCancel'
        )}
      </Button>

      {!successMessage && (
        <Button
          disabled={isConnecting || isInvalidFields}
          onClick={handleSubmit}
        >
          {isConnecting || isLoadingIntegration ? (
            <span className="integration-loading" />
          ) : (
            saveIntegrationButtonText
          )}
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      className="integrationModal"
      isOpen={isOpen}
      title={title}
      crossAxisBtn={false}
      footer={ModalFooter}
      closeModalFn={identity}
    >
      {ModalBody}
    </Modal>
  );
}

LocalzIntegrationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  integrationData: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  confirmActionCallback: PropTypes.func.isRequired,
  cancelActionCallback: PropTypes.func.isRequired
};

export default memo(LocalzIntegrationModal);
